import { useMutation } from '@apollo/client';
import { ReportProblemOutlined, Campaign, Email } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import { Tooltip, Alert, Button } from '@mui/material';
import { Box } from '@mui/system';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import START_LOTTERY_MUTATION from 'gql/mutations/ModuleSelectionLotteryService/startLottery.gql';
import ARCHIVE_SELECTION_MUTATION from 'gql/mutations/ModuleSelectionsService/changeModuleSelectionState.gql';
import PUBLISH_MODULE_SELECTION from 'gql/mutations/ModuleSelectionsService/publishModuleSelection.gql';
import SEND_EMAIL_NOTIFICATION from 'gql/mutations/ModuleSelectionsService/sendEmailNotificationForSelection.gql';

import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { ModuleWithComplete } from 'app/containers/AWPM-FWPM/Modules/models';
import { Lottery, MutationStartLotteryArgs } from 'app/containers/AWPM-FWPM/StudentView/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { ReactComponent as CalendarIcon } from 'assets/metro-calendar.svg';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { SelectionTabPanels } from '..';
import { ModuleSelection, State } from '../../models';

interface Props {
  modules: ModuleWithComplete[] | undefined;
  moduleSelection: ModuleSelection;
  setTab: (value: SelectionTabPanels) => void;
}

export default function SelectionAdminButtons({ modules, moduleSelection, setTab }: Props) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);

  const [confirmStartFinalCalculation, setConfirmStartFinalCalculation] = useState(false);
  const [confirmPublishResutsOpen, setConfirmPublishResultsOpen] = useState(false);
  const [confirmArchiveSelectionOpen, setConfirmArchiveSelectionOpen] = useState(false);
  const [confirmSendEmailDialogOpen, setConfirmSendEmailDialogOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  const [sendEmailNotification] = useMutation(SEND_EMAIL_NOTIFICATION, {
    context: { clientName: 'federation' }
  });

  const [startLottery] = useMutation<Lottery, MutationStartLotteryArgs>(START_LOTTERY_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [publishSeleciton] = useMutation(PUBLISH_MODULE_SELECTION, {
    context: { clientName: 'federation' }
  });

  const [changeSelectionState] = useMutation(ARCHIVE_SELECTION_MUTATION, {
    context: { clientName: 'federation' }
  });

  const handleStartSelection = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await startLottery({
        variables: { moduleSelectionId: Number(moduleSelection.id), isFinalRun: true }
      });
      dispatch(AlertActions.success(t(translations.pages.moduleSelectionLottery.startSuccess())));
      setTab(SelectionTabPanels.OCCUPANCY);
    } catch (e) {
      dispatch(AlertActions.error(t(translations.pages.moduleSelectionLottery.startFailed())));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendEmailNotificationConfirm = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await sendEmailNotification({
        variables: { id: parseInt(moduleSelection.id, 10) },
        refetchQueries: ['getModuleSelectionById']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.moduleSelectionsDetail.modulePreview.success.sendEmailNotification())
        )
      );
    } catch (e) {
      dispatch(
        AlertActions.error(
          t(translations.pages.moduleSelectionsDetail.modulePreview.error.sendEmailNotification())
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishResults = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await publishSeleciton({
        variables: {
          id: parseInt(moduleSelection.id, 10)
        },
        refetchQueries: ['getModuleSelectionById']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.moduleSelectionsDetail.modulePreview.success.publishSelection())
        )
      );
    } catch (e) {
      dispatch(
        AlertActions.error(
          t(translations.pages.moduleSelectionsDetail.modulePreview.error.publishSelection())
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveSelection = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await changeSelectionState({
        variables: {
          id: parseInt(moduleSelection.id, 10),
          state: State.Archived
        },
        refetchQueries: ['getModuleSelectionById']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.moduleSelectionsDetail.modulePreview.success.archiveSelection())
        )
      );
    } catch (e) {
      dispatch(
        AlertActions.error(
          t(translations.pages.moduleSelectionsDetail.modulePreview.error.archiveSelection())
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isStartSelectionDisabled: boolean =
    !modules?.length ||
    !!modules.find(module => !module.complete) ||
    moduleSelection.state !== State.Started;
  const isSelectionPublishDisabled: boolean =
    !!moduleSelection?.publishedAt ||
    !(moduleSelection.state === State.Dischargable || moduleSelection.state === State.Completed);
  const isSelectionArchiveDisabled: boolean =
    moduleSelection.state !== State.Completed ||
    !(aclHelper.isSelectionAdmin() || aclHelper.isSecretaryUser(true));
  const isSelectionEmailNotificationDisabled: boolean = !!(
    !moduleSelection.publishedAt || moduleSelection.studentsAlreadyNotified
  );
  return (
    <>
      {/* Start Selection Button */}
      <Box className="previewTab__heading-box__actions">
        <Tooltip
          disableInteractive
          disableFocusListener={!isStartSelectionDisabled}
          disableHoverListener={!isStartSelectionDisabled}
          disableTouchListener={!isStartSelectionDisabled}
          classes={{ tooltip: 'service-health__tooltip' }}
          placement="top-start"
          title={
            <>
              <Alert severity="warning" elevation={2} icon={false}>
                {t(translations.pages.moduleSelectionsDetail.modulePreview.startSelectionError())}
              </Alert>
            </>
          }
        >
          <span>
            <Button
              variant="text"
              startIcon={<CalendarIcon />}
              onClick={() => {
                setConfirmStartFinalCalculation(true);
              }}
              disabled={isStartSelectionDisabled}
            >
              {t(translations.pages.moduleSelectionsDetail.modulePreview.startSelection())}
              {isStartSelectionDisabled && (
                <ReportProblemOutlined className="service-health__icon service-health__icon--warning" />
              )}
            </Button>
          </span>
        </Tooltip>
        {/* Publish Button */}
        <Tooltip
          disableInteractive
          disableFocusListener={!isSelectionPublishDisabled}
          disableHoverListener={!isSelectionPublishDisabled}
          disableTouchListener={!isSelectionPublishDisabled}
          placement="top-start"
          title={
            <>
              <Alert severity="warning" elevation={2} icon={false}>
                {t(translations.pages.moduleSelectionsDetail.modulePreview.publishSelectionError())}
              </Alert>
            </>
          }
        >
          <span>
            <Button
              variant="text"
              size="large"
              disabled={isSelectionPublishDisabled}
              startIcon={<Campaign />}
              onClick={() => setConfirmPublishResultsOpen(true)}
            >
              {t(translations.pages.moduleSelectionsDetail.modulePreview.publishSelection())}
            </Button>
          </span>
        </Tooltip>
        {/* Email Notification Button */}
        <Tooltip
          disableInteractive
          disableFocusListener={!isSelectionEmailNotificationDisabled}
          disableHoverListener={!isSelectionEmailNotificationDisabled}
          disableTouchListener={!isSelectionEmailNotificationDisabled}
          placement="top-start"
          title={
            <>
              <Alert severity="warning" elevation={2} icon={false}>
                {t(
                  translations.pages.moduleSelectionsDetail.modulePreview.startEmailNotificationError[
                    moduleSelection.studentsAlreadyNotified ? 'alreadyNotified' : 'notYetPublished'
                  ]()
                )}
              </Alert>
            </>
          }
        >
          <span>
            <Button
              variant="text"
              size="large"
              disabled={isSelectionEmailNotificationDisabled}
              startIcon={<Email />}
              onClick={() => setConfirmSendEmailDialogOpen(true)}
            >
              {t(translations.pages.moduleSelections.startEmailNotification())}
            </Button>
          </span>
        </Tooltip>
        {/* Archive Button */}
        <Tooltip
          disableInteractive
          disableFocusListener={!isSelectionArchiveDisabled}
          disableHoverListener={!isSelectionArchiveDisabled}
          disableTouchListener={!isSelectionArchiveDisabled}
          placement="top-start"
          title={
            <>
              <Alert severity="warning" elevation={2} icon={false}>
                {t(translations.pages.moduleSelectionsDetail.modulePreview.archiveSelectionError())}
              </Alert>
            </>
          }
        >
          <span>
            <Button
              variant="text"
              size="large"
              disabled={isSelectionArchiveDisabled}
              startIcon={<ArchiveIcon />}
              onClick={() => setConfirmArchiveSelectionOpen(true)}
            >
              {t(translations.pages.moduleSelectionsDetail.modulePreview.archiveSelection())}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <ConfirmationDialog
        open={confirmStartFinalCalculation}
        title={t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.finalSelectionTitle()
        )}
        onClose={() => {
          setConfirmStartFinalCalculation(false);
        }}
        onConfirm={() => {
          setConfirmStartFinalCalculation(false);
          handleStartSelection();
        }}
      >
        <div>
          {t(translations.pages.moduleSelectionsDetail.modulePreview.confirm.finalSelectionBody())}
        </div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={confirmPublishResutsOpen}
        title={t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.publishSelectionTitle()
        )}
        onClose={() => {
          setConfirmPublishResultsOpen(false);
        }}
        onConfirm={() => {
          setConfirmPublishResultsOpen(false);
          handlePublishResults();
        }}
      >
        <div>
          {t(
            translations.pages.moduleSelectionsDetail.modulePreview.confirm.publishSelectionBody()
          )}
        </div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={confirmArchiveSelectionOpen}
        title={t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.archiveSelectionTitle()
        )}
        onClose={() => {
          setConfirmArchiveSelectionOpen(false);
        }}
        onConfirm={() => {
          setConfirmArchiveSelectionOpen(false);
          handleArchiveSelection();
        }}
      >
        <div>
          {t(
            translations.pages.moduleSelectionsDetail.modulePreview.confirm.archiveSelectionBody()
          )}
        </div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={confirmSendEmailDialogOpen}
        onClose={() => setConfirmSendEmailDialogOpen(false)}
        title={t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.startEmailNotificationTitle(),
          {
            title: moduleSelection.title
          }
        )}
        onConfirm={() => {
          setConfirmSendEmailDialogOpen(false);
          handleSendEmailNotificationConfirm();
        }}
      >
        {t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.startEmailNotificationBody()
        )}
      </ConfirmationDialog>
    </>
  );
}
